// Pure chart-model builders extracted from the Analytics/role-dashboard components so the exact
// series/labels/state each chart feeds to Recharts is unit-testable (chartModels.test.js) without
// jsdom SVG layout. Each builder replicates the component's inline transform byte-for-byte; the
// components import these, so production output is unchanged. Values are never fabricated — a real
// zero stays zero, an empty source yields [], missing fields do not become nonzero.

const num = (v) => Number(v) || 0;

// Director daily "Resolution vs Escalation" bar chart.
export function buildDailyTrendModel(dailyMetrics) {
  return (dailyMetrics || []).map((d) => ({
    date: d.date,
    Resolved: d.resolved || 0,
    Escalated: d.escalated || 0,
    Tickets: d.tickets || 0,
  }));
}

// Director workload "Ticket Status Distribution" pie (only nonzero slices).
export function buildStatusDistributionModel(biz) {
  if (!biz) return [];
  return [
    { name: 'Resolved', value: biz.resolved_tickets || 0, color: '#10b981' },
    { name: 'Escalated', value: Math.round(((biz.escalation_rate || 0) / 100) * (biz.total_tickets || 0)), color: '#ef4444' },
    { name: 'Other', value: Math.max(0, (biz.total_tickets || 0) - (biz.resolved_tickets || 0)), color: '#f59e0b' },
  ].filter((s) => s.value > 0);
}

// Generic intent bars {name,count} sorted desc (Director "Conversation Intents").
export function buildIntentBarModel(intentDistribution) {
  return Object.entries(intentDistribution || {})
    .map(([name, count]) => ({ name, count: num(count) }))
    .sort((a, b) => b.count - a.count);
}

// Strategy "Resolution vs Escalation Rate" line chart (percentages).
export function buildRatePercentTrendModel(dailyMetrics) {
  return (dailyMetrics || []).map((d) => ({
    date: d.date?.slice(5),
    'Resolution %': d.tickets > 0 ? Math.round((d.resolved / d.tickets) * 100) : 0,
    'Escalation %': d.tickets > 0 ? Math.round((d.escalated / d.tickets) * 100) : 0,
  }));
}

// Strategy "Customer Intent Distribution" bars — formatted names, top 8.
export function buildFormattedIntentBarModel(intentDistribution) {
  return Object.entries(intentDistribution || {})
    .map(([name, count]) => ({
      name: name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()).slice(0, 18),
      count,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);
}

// Executive Strategic "Ticket Volume/Resolution Trend" bar chart.
export function buildVolumeTrendModel(dailyMetrics) {
  return (dailyMetrics || []).map((d) => ({
    date: d.date?.slice(5) || '',
    Tickets: d.tickets || 0,
    Resolved: d.resolved || 0,
    Escalated: d.escalated || 0,
  }));
}

// Analytics overview daily series {resolved,escalated,pending,tickets}.
export function buildAnalyticsDailySeriesModel(dailyMetrics) {
  return (dailyMetrics || []).map((d) => ({
    date: d.date,
    resolved: d.resolved || 0,
    escalated: d.escalated || 0,
    pending: Math.max(0, (d.tickets || 0) - (d.resolved || 0) - (d.escalated || 0)),
    tickets: d.tickets || 0,
  }));
}

// Analytics customer "Languages"/"Intents" distribution bars {name,count} sorted desc.
export function buildDistributionModel(distribution) {
  return Object.entries(distribution || {})
    .map(([name, count]) => ({ name, count: num(count) }))
    .sort((a, b) => b.count - a.count);
}

// Analytics "Escalation by Level" bar chart — real levels only; empty => [] (honest empty state).
export function buildEscalationByLevelModel(escalationByLevel) {
  if (!escalationByLevel || Object.keys(escalationByLevel).length === 0) return [];
  return Object.entries(escalationByLevel).map(([level, count]) => ({
    level: level.charAt(0).toUpperCase() + level.slice(1),
    count,
  }));
}

// AIAnalytics "Hourly/Daily Performance" bars {hour,count}.
export function buildHourlyPerformanceModel(dailyVolume) {
  return Object.entries(dailyVolume || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, count]) => ({ hour: label, count }));
}

// AIAnalytics "Top Categories" {category,count,percentage}.
export function buildTopCategoriesModel(catCounts, total) {
  return Object.entries(catCounts || {})
    .map(([category, count]) => ({ category, count, percentage: total ? Math.round((count / total) * 100) : 0 }))
    .sort((a, b) => b.count - a.count);
}

// Analyst Analytics Dashboard resolution-trend area chart {resolutionRate,escalationRate,tickets}.
export function buildResolutionRateTrendModel(dailyMetrics) {
  return (dailyMetrics || []).map((d) => ({
    date: d.date,
    resolutionRate: d.tickets > 0 ? Math.round((d.resolved / d.tickets) * 100) : 0,
    escalationRate: d.tickets > 0 ? Math.round((d.escalated / d.tickets) * 100) : 0,
    tickets: d.tickets,
  }));
}

// AIMetricsDashboard "Language Distribution" pie {name,value} (capitalized language name).
export function buildAILanguageDistributionModel(languageDistribution) {
  return Object.entries(languageDistribution || {}).map(([key, value]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    value,
  }));
}

// AIMetricsDashboard "Intent Distribution" bars {name,count} (first-underscore replaced, word-cased).
export function buildAIIntentDistributionModel(intentDistribution) {
  return Object.entries(intentDistribution || {}).map(([key, value]) => ({
    name: key.replace('_', ' ').split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    count: value,
  }));
}

// AIMetricsDashboard "Escalation by Level" bars {name,count} (capitalized level).
export function buildAIEscalationDistributionModel(escalationByLevel) {
  return Object.entries(escalationByLevel || {}).map(([key, value]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    count: value,
  }));
}

// Analyst Dashboard "Solution Bank Stats" bars — fixed 3 rows; i18n labels passed in (no t() in builder).
// The component still gates rendering on solutionStats.total > 0; this only builds the row array.
export function buildSolutionStatsChartModel(solutionStats, labels) {
  const s = solutionStats || {};
  const l = labels || {};
  return [
    { name: l.total, count: s.total, fill: '#4A90D9' },
    { name: l.approved, count: s.approved, fill: '#4CAF50' },
    { name: l.pending, count: s.pending, fill: '#FF9800' },
  ];
}

// Analyst Dashboard "Intent Distribution" bars with percentage {name,count,pct}, top 8.
export function buildAnalystIntentPercentageModel(intentDistribution, totalConversations) {
  return Object.entries(intentDistribution || {})
    .map(([name, count]) => ({
      name: name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
      count,
      pct: totalConversations > 0 ? Math.round((count / totalConversations) * 100) : 0,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);
}

// Analytics "Tickets by Category" aggregation feeding 3 chart instances {name,count,resolved,value,resolutionRate}.
// Tenant/period filtering stays in the component; this aggregates already-filtered tickets only.
export function buildTicketsByCategoryModel(filteredTickets, resolvedStates) {
  const list = Array.isArray(filteredTickets) ? filteredTickets : [];
  const resolved = resolvedStates || [];
  return Object.values(
    list.reduce((acc, t) => {
      const k = t.category || t.category_name || 'Uncategorized';
      acc[k] = acc[k] || { name: k, count: 0, resolved: 0 };
      acc[k].count += 1;
      if (resolved.includes(String(t.status || '').toLowerCase())) acc[k].resolved += 1;
      return acc;
    }, {})
  )
    .map((c) => ({
      ...c,
      value: list.length ? Math.round((c.count / list.length) * 100) : 0,
      resolutionRate: c.count ? Math.round((c.resolved / c.count) * 100) : 0,
    }))
    .sort((a, b) => b.count - a.count);
}

// Analytics "Resolution Rate Buckets" pie — nonzero buckets only; i18n labels passed in (no t() in builder).
export function buildResolutionBucketsModel(ticketsByCategory, labels) {
  const tbc = Array.isArray(ticketsByCategory) ? ticketsByCategory : [];
  const l = labels || {};
  return [
    { name: l.excellent, value: tbc.filter((c) => c.resolutionRate > 90).length, color: '#10b981' },
    { name: l.good, value: tbc.filter((c) => c.resolutionRate >= 70 && c.resolutionRate <= 90).length, color: '#3b82f6' },
    { name: l.needsImprovement, value: tbc.filter((c) => c.resolutionRate < 70).length, color: '#f59e0b' },
  ].filter((b) => b.value > 0);
}
